import { useState } from 'react'
import { ChevronRight, Loader2, LogIn, UserMinus, UserPlus, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useDeleteAgent, useGrantDashboardAccess, type InterfaceAgent } from '@/api/team'
import { AvatarPersonne } from '@/components/ui/AvatarPersonne'
import { MemberSheet, fullName, roleOf } from './MemberSheet'

interface Props {
  agents: InterfaceAgent[]
  onInvite: () => void
}

/**
 * The team: one plain row per person (photo, name, email, role). A click
 * opens the member's panel, where the role, the recording exception and the
 * removal live.
 */
export function AgentsList({ agents, onInvite }: Props) {
  const { t } = useTranslation()
  const deleteAgent = useDeleteAgent()
  // The id, not the object: the panel follows the list as changes save.
  const [ouvertId, setOuvertId] = useState<number | null>(null)
  const [pendingDelete, setPendingDelete] = useState<InterfaceAgent | null>(null)
  const [pendingAccess, setPendingAccess] = useState<InterfaceAgent | null>(null)
  const grant = useGrantDashboardAccess()
  const ouvert = agents.find((a) => a.id === ouvertId) ?? null

  const handleConfirmAccess = async () => {
    if (!pendingAccess) return
    try {
      const r = await grant.mutateAsync(pendingAccess.id)
      toast.success(t('manager.team.list.dashboardAccessSent', { email: r.email }))
    } catch {
      toast.error(t('manager.team.list.dashboardAccessError'))
    } finally {
      setPendingAccess(null)
    }
  }

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return
    try {
      await deleteAgent.mutateAsync(pendingDelete.id)
      toast.success(t('recordingAccess.removed', { name: fullName(pendingDelete) || pendingDelete.user_email }))
      setOuvertId(null)
    } catch {
      toast.error(t('manager.team.list.deleteError'))
    } finally {
      setPendingDelete(null)
    }
  }

  if (agents.length === 0) {
    return (
      <div className="text-center py-20">
        <Users className="h-12 w-12 text-slate-200 dark:text-slate-700 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
          {t('manager.team.list.emptyTitle')}
        </h3>
        <p className="text-sm text-slate-400 dark:text-slate-500 mb-6 max-w-sm mx-auto">
          {t('manager.team.list.emptyDescription')}
        </p>
        <Button
          onClick={onInvite}
          className="bg-[#0A192F] text-white hover:bg-[#0A192F]/90 dark:bg-white dark:text-[#0A192F]"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          {t('manager.team.inviteCta')}
        </Button>
      </div>
    )
  }

  return (
    <div>
      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {agents.map((agent) => {
          const nom = fullName(agent) || agent.user_email
          return (
            <li key={agent.id}>
              <button
                type="button"
                onClick={() => setOuvertId(agent.id)}
                aria-label={t('recordingAccess.openMember', { name: nom })}
                className="w-full flex items-center gap-4 py-3.5 px-2 text-left rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
              >
                <AvatarPersonne nom={nom} photo={agent.avatar_url} className="w-10 h-10" />
                <span className="flex-1 min-w-0">
                  <span className="block text-[14px] font-medium text-slate-900 dark:text-white truncate">{nom}</span>
                  <span className="block text-[13px] text-slate-400 dark:text-slate-500 truncate">{agent.user_email}</span>
                </span>
                <span className="text-[13px] text-slate-500 dark:text-slate-400 flex-shrink-0">
                  {t(`recordingAccess.roles.${roleOf(agent)}.label`)}
                </span>
                <ChevronRight className="h-4 w-4 text-slate-300 dark:text-slate-600 flex-shrink-0" aria-hidden />
              </button>
            </li>
          )
        })}
      </ul>

      <MemberSheet
        agent={ouvert}
        onClose={() => setOuvertId(null)}
        onRemove={setPendingDelete}
        onGrantAccess={setPendingAccess}
      />

      <AlertDialog open={!!pendingAccess} onOpenChange={(open) => !open && setPendingAccess(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('manager.team.list.accessDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAccess &&
                t('manager.team.list.accessDialog.description', {
                  name: fullName(pendingAccess) || pendingAccess.user_email,
                  email: pendingAccess.user_email,
                })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="text-sm text-slate-600 dark:text-slate-300 list-disc pl-5 space-y-1">
            <li>{t('manager.team.list.accessDialog.point1')}</li>
            <li>{t('manager.team.list.accessDialog.point2')}</li>
            <li>{t('manager.team.list.accessDialog.point3')}</li>
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={grant.isPending}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleConfirmAccess()
              }}
              disabled={grant.isPending}
            >
              {grant.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogIn className="h-4 w-4 mr-2" />}
              {t('manager.team.list.accessDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingDelete && t('recordingAccess.removeTitle', { name: fullName(pendingDelete) || pendingDelete.user_email })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('recordingAccess.removeDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteAgent.isPending}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleConfirmDelete()
              }}
              disabled={deleteAgent.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteAgent.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserMinus className="h-4 w-4 mr-2" />}
              {t('recordingAccess.removeConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
